/**
 * Branch selector component
 */

import { useState, type ReactElement } from 'react';
import { useGitBranches } from '../../git/hooks/useGitBranches';
import type { GitBranch } from '../../git/types/git.types';

interface BranchSelectorProps {
  serverId: string | null;
  externalRef: string | null;
  selectedBranch: string | null;
  onSelect: (branch: string | null) => void;
  disabled?: boolean;
}

export function BranchSelector({
  serverId,
  externalRef,
  selectedBranch,
  onSelect,
  disabled = false,
}: BranchSelectorProps): ReactElement {
  const [isOpen, setIsOpen] = useState(false);

  const { data, isLoading, error } = useGitBranches(serverId, externalRef);

  const isDisabled = disabled || !serverId || !externalRef;

  const handleSelect = (branch: GitBranch): void => {
    onSelect(branch.name);
    setIsOpen(false);
  };

  return (
    <div className="form-select">
      <button
        type="button"
        onClick={() => { setIsOpen(!isOpen); }}
        disabled={isDisabled}
        className="form-select__trigger"
      >
        {selectedBranch ? (
          <span className="form-select__value">{selectedBranch}</span>
        ) : (
          <span className="form-select__placeholder">Select branch...</span>
        )}
        <span className="form-select__chevron">{isOpen ? '▲' : '▼'}</span>
      </button>

      {isOpen && !isDisabled && (
        <div className="form-select__dropdown">
          {isLoading && (
            <div className="form-select__message">Loading...</div>
          )}

          {error && (
            <div className="form-select__message form-select__error">
              {error.message}
            </div>
          )}

          {data?.branches.length === 0 && !isLoading && (
            <div className="form-select__message">No branches found</div>
          )}

          {data?.branches.map((branch) => (
            <button
              key={branch.name}
              type="button"
              onClick={() => { handleSelect(branch); }}
              className="form-select__option"
            >
              <span className="form-select__option-text">{branch.name}</span>
              <span style={{ display: 'flex', gap: '0.25rem' }}>
                {branch.name === data.default_branch && (
                  <span className="form-select__badge form-select__badge--default">
                    default
                  </span>
                )}
                {branch.protected && (
                  <span className="form-select__badge form-select__badge--protected">
                    protected
                  </span>
                )}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
